import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AdjustedRenewalSummary } from './adjustedRenewalSummary';
import './mobileAdjustedRenewalSummarySticky.scss';

export const MobileAdjustedRenewalSummarySticky = ({ isVisible, renewalSummary, cmsData, isLoading }) => {
  const [mobileStickyFooterOffset, setMobileStickyFooterOffset] = useState(0);
  const [mobileStickyMaxHeight, setMobileStickyMaxHeight] = useState(null);
  const [isDockedAtBottom, setIsDockedAtBottom] = useState(false);
  const stickySummaryRef = useRef(null);

  useEffect(() => {
    if (!isVisible) {
      setMobileStickyFooterOffset(0);
      setMobileStickyMaxHeight(null);
      setIsDockedAtBottom(false);
      return undefined;
    }

    let frameId = null;

    const updateFooterOffset = () => {
      frameId = null;
      const footerElement = document.querySelector('.mainFooter');
      const brandSelectorElement = document.querySelector('.brandSelector');
      const brandSelectorRect = brandSelectorElement?.getBoundingClientRect();
      const isBrandSelectorVisible =
        !!brandSelectorRect && brandSelectorRect.bottom > 0 && brandSelectorRect.top < window.innerHeight;
      const brandSelectorBottomClearance = isBrandSelectorVisible ? Math.round(brandSelectorRect.height + 8) : 0;
      const footerRect = footerElement?.getBoundingClientRect();
      const hasReachedFooterBottom = !!footerRect && footerRect.bottom <= window.innerHeight + 2;
      const hasReachedPageBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

      if (hasReachedFooterBottom || hasReachedPageBottom) {
        setIsDockedAtBottom(true);
        setMobileStickyFooterOffset(0);
        setMobileStickyMaxHeight(null);
        return;
      }

      setIsDockedAtBottom(false);

      if (!footerElement) {
        setMobileStickyFooterOffset(brandSelectorBottomClearance);
        setMobileStickyMaxHeight(Math.max(320, Math.round(window.innerHeight - 24 - brandSelectorBottomClearance)));
        return;
      }

      const nextOffset = brandSelectorBottomClearance;
      const nextMaxHeight = Math.max(320, Math.round(window.innerHeight - 24 - nextOffset));

      setMobileStickyFooterOffset((currentOffset) => (currentOffset === nextOffset ? currentOffset : nextOffset));
      setMobileStickyMaxHeight((currentMaxHeight) =>
        currentMaxHeight === nextMaxHeight ? currentMaxHeight : nextMaxHeight
      );
    };

    const scheduleUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateFooterOffset);
    };

    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [isVisible]);

  if (!isVisible || !renewalSummary) {
    return null;
  }

  return (
    <div
      ref={stickySummaryRef}
      className={`mobileAdjustedRenewalSummarySticky ${isDockedAtBottom ? 'isDocked' : ''}`}
      style={{
        '--mobileStickyFooterOffset': `${mobileStickyFooterOffset}px`,
        '--mobileStickyMaxHeight': mobileStickyMaxHeight != null ? `${mobileStickyMaxHeight}px` : undefined
      }}
    >
      <AdjustedRenewalSummary
        renewalData={renewalSummary}
        isLoading={isLoading}
        cmsData={cmsData}
      />
    </div>
  );
};

MobileAdjustedRenewalSummarySticky.propTypes = {
  isVisible: PropTypes.bool,
  renewalSummary: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  cmsData: PropTypes.object
};

MobileAdjustedRenewalSummarySticky.defaultProps = {
  isVisible: false,
  renewalSummary: null,
  isLoading: false,
  cmsData: null
};
